/**
 * RootMD Video Gallery Component
 * Handles video lightbox modal, navigation, and product add-to-cart
 */

class RootMDVideoGallery {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.modal = document.getElementById(`rootmd-video-modal-${sectionId}`);

    if (!this.modal) return;

    this.currentVideoIndex = 0;
    this.videoTriggers = document.querySelectorAll('[data-video-trigger]');
    this.videoPlayers = this.modal.querySelectorAll('[data-video-player]');
    this.videoNavThumbs = this.modal.querySelectorAll('[data-video-nav]');
    this.productLists = this.modal.querySelectorAll('[data-products-for-video]');
    this.closeButtons = this.modal.querySelectorAll('[data-modal-close]');

    this.init();
  }

  init() {
    // Bind event listeners
    this.videoTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        const videoIndex = parseInt(trigger.dataset.videoTrigger);
        this.openModal(videoIndex);
      });
    });

    this.closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.closeModal());
    });

    this.videoNavThumbs.forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        const videoIndex = parseInt(thumb.dataset.videoNav);
        this.switchVideo(videoIndex);
      });
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.closeModal();
      }
    });

    // Initialize product forms
    this.initProductForms();
  }

  openModal(videoIndex = 0) {
    this.currentVideoIndex = videoIndex;
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';

    this.switchVideo(videoIndex);

    // Play video if autoplay is desired
    const activeVideo = this.videoPlayers[videoIndex]?.querySelector('.video-element');
    if (activeVideo) {
      activeVideo.play().catch(err => console.log('Autoplay prevented:', err));
    }
  }

  closeModal() {
    this.modal.classList.remove('active');
    document.body.style.overflow = '';

    // Pause all videos
    this.videoPlayers.forEach(player => {
      const video = player.querySelector('.video-element');
      if (video) {
        video.pause();
      }
    });
  }

  switchVideo(videoIndex) {
    this.currentVideoIndex = videoIndex;

    // Update active video player
    this.videoPlayers.forEach((player, idx) => {
      if (idx === videoIndex) {
        player.classList.add('active');
        // Lazy load video source if not already loaded
        const video = player.querySelector('.video-element');
        const videoSrc = video?.dataset.videoSrc;
        if (video && videoSrc && !video.src) {
          video.src = videoSrc;
        }
      } else {
        player.classList.remove('active');
        // Pause other videos
        const video = player.querySelector('.video-element');
        if (video) video.pause();
      }
    });

    // Update active thumbnail
    this.videoNavThumbs.forEach((thumb, idx) => {
      if (idx === videoIndex) {
        thumb.classList.add('active');
      } else {
        thumb.classList.remove('active');
      }
    });

    // Update active product list
    this.productLists.forEach((list, idx) => {
      if (idx === videoIndex) {
        list.classList.add('active');
      } else {
        list.classList.remove('active');
      }
    });
  }

  initProductForms() {
    const productForms = this.modal.querySelectorAll('[data-product-form]');

    productForms.forEach(form => {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleAddToCart(form);
      });
    });
  }

  async handleAddToCart(form) {
    const submitButton = form.querySelector('[data-product-atc]');
    const buttonText = submitButton.querySelector('span:first-child');
    const spinner = submitButton.querySelector('.loading-spinner');

    // Disable button and show loading
    submitButton.disabled = true;
    spinner?.classList.remove('hidden');

    // Get form data
    const formData = new FormData(form);
    const variantId = formData.get('id');
    const quantity = formData.get('quantity') || 1;

    try {
      // Add to cart via Shopify Ajax API
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: parseInt(quantity)
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const data = await response.json();

      // Show success feedback
      buttonText.textContent = 'Added!';
      setTimeout(() => {
        buttonText.textContent = 'SHOP NOW';
      }, 2000);

      // Trigger cart update event (for cart drawer/notification)
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { item: data }
      }));

      // Update cart count if element exists
      this.updateCartCount();

    } catch (error) {
      console.error('Add to cart error:', error);
      buttonText.textContent = 'Error - Try Again';
      setTimeout(() => {
        buttonText.textContent = 'SHOP NOW';
      }, 2000);
    } finally {
      // Re-enable button and hide loading
      submitButton.disabled = false;
      spinner?.classList.add('hidden');
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart.js');
      const cart = await response.json();

      // Update cart count in header (if element exists)
      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(el => {
        el.textContent = cart.item_count;
      });

    } catch (error) {
      console.error('Failed to update cart count:', error);
    }
  }
}

// Initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  // Find all video gallery sections (in case there are multiple)
  const videoGallerySections = document.querySelectorAll('.rootmd-video-gallery-section');

  videoGallerySections.forEach(section => {
    // Extract section ID from parent product-hero-banner
    const heroSection = section.closest('.product-hero-banner');
    if (heroSection) {
      const sectionId = heroSection.className.match(/product-hero-banner-(\S+)/)?.[1];
      if (sectionId) {
        new RootMDVideoGallery(sectionId);
      }
    }
  });
});
